import React, { Component } from 'react'
import { connect } from 'react-redux'

import Style from './ThreatFeedDetections.module.css'
import LoaderWrapper from '../../common/LoaderWrapper/LoaderWrapper'
import Loading from '../../common/LoaderWrapper/loading'
import Paginator from '../../common/paginator/paginator'
import Tooltip from '../../common/popover/tooltip'
import ThreatFeedStatusMenu from './ThreatFeedStatusMenu'
import { formatThreatFeedDatetime } from '../../../utilities/datetime'
import { getOSType } from '../../../utilities/OSTypes'
import {
  loadNextEvents,
  loadPrevEvents,
  loadEvents,
  setThreatFiltersInStore,
} from '../../../store/entities/events'
import { isGlobalView } from '../../../utilities/config'

// ===========================================================================
// Threat Feed — list view (PIXMC-6500).
//
// Custom table markup ported from the claude-dashboards mockup (the client asked for this
// list to match the mockup, not reuse IncidentList). Data, pagination and the Update Status
// dropdown stay on the shared events slice — this is a presentation-only, isolated UI.
// Renders inside ThreatFeed's card (no own card wrapper).
// ===========================================================================

const STATUS_CLASS = {
  'PIXM Verified': Style.statusPixmVerified,
  'Verified Phish': Style.statusVerifiedPhish,
  'AI Suspicious': Style.statusAiSuspicious,
  'Reported Phish': Style.statusReportedPhish,
}
const badgeClass = (status) => STATUS_CLASS[status] || Style.statusDefault

const userName = (e) => e.osUserId || e.osComputerName || e.osuuid || e.email || 'NA'

class ThreatFeedTable extends Component {
  // Opened is the only sortable column (client decision); flips the `order` param the
  // all-events API already honours (ORDER BY DetectionTime asc/desc) and reloads.
  toggleSort = () => {
    const { threatFilters, selectedCustomer } = this.props
    if (!selectedCustomer) return
    const order = threatFilters.order === 'asc' ? 'desc' : 'asc'
    const next = { ...threatFilters, order, offset: 0 }
    this.props.setThreatFiltersInStore(next)
    this.props.loadEvents(
      next,
      selectedCustomer.id,
      selectedCustomer.defaultCustomerId,
      isGlobalView(selectedCustomer.organizationName),
    )
  }

  render() {
    const { events, threatFilters, totalSize, loading, isLastPage, onDetails } = this.props
    const { offset } = threatFilters
    const rows = events.slice(offset, offset + 20)
    const asc = threatFilters.order === 'asc'
    // Remount the Paginator when any filter (except offset) changes so its internally-cached
    // filters stay fresh — otherwise paging re-sends the previous startTime (stale filters).
    const pagerKey = [
      threatFilters.startTime, threatFilters.endTime, threatFilters.order,
      threatFilters.brand || '', threatFilters.extensionUserId || '',
      threatFilters.triagedEvent != null ? threatFilters.triagedEvent : '',
      threatFilters.pixmVerified != null ? threatFilters.pixmVerified : '',
      (threatFilters.type || []).join(','),
    ].join('|')

    return (
      <>
        <div className={Style.resultsCount}>
          Results ({rows.length === 0 ? 0 : isLastPage ? offset + rows.length : `More than ${offset + rows.length}`})
        </div>
        <div className={Style.cardBody}>
          {/* Loader overlays the table only (existing Loading component); content stays
              mounted underneath during filter / sort / page loads. */}
          {loading && <div className={Style.tableLoaderOverlay}><Loading loading={true} /></div>}
          <LoaderWrapper
            loading={false}
            data={rows}
            styles={{ minHeight: '400px' }}
            placeholderText={'No Threats Detected'}
          >
            <>
              <div className={Style.tableWrap}>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Brand</th>
                      <th>URL</th>
                      <th className={Style.sortable} onClick={this.toggleSort}>
                        Opened
                        <svg
                          className={`${Style.sortCaret} ${asc ? Style.sortAsc : ''}`}
                          viewBox="0 0 10 6"
                          aria-hidden="true"
                        >
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </th>
                      <th>Action</th>
                      <th>Status</th>
                      <th aria-label="Row actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>
                          <span className={Style.userTip}>
                            {userName(item)}
                            <span className={Style.userPopup}>
                              <span className={Style.userDetail}><span className={Style.userDetailLabel}>Email</span>{item.email || 'NA'}</span>
                              <span className={Style.userDetail}><span className={Style.userDetailLabel}>Browser</span>{item.browserName || 'NA'}</span>
                              <span className={Style.userDetail}><span className={Style.userDetailLabel}>OS</span>{getOSType(item.os) || item.os || 'NA'}</span>
                              <span className={Style.userDetail}><span className={Style.userDetailLabel}>Device ID</span>{item.osuuid || 'NA'}</span>
                            </span>
                          </span>
                        </td>
                        <td><span className={Style.brandText}>{item.brands || 'NA'}</span></td>
                        <td className={Style.urlCell} data-tip data-for={item.url}>
                          {item.url}
                          <Tooltip id={item.url} data={item.url} className="tfTooltipWrap" />
                        </td>
                        <td>{formatThreatFeedDatetime(item.detectionTime)}</td>
                        <td>{item.userAction}</td>
                        <td><span className={badgeClass(item.status)}>{item.status}</span></td>
                        <td>
                          <div className={Style.actionsCell}>
                            <ThreatFeedStatusMenu event={item} onAction={this.props.onStatusAction} />
                            <button className={Style.btn} onClick={() => onDetails(item)}>View Details</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={Style.pager}>
                <Paginator
                  key={pagerKey}
                  onNext={this.props.loadNextEvents}
                  onPrev={this.props.loadPrevEvents}
                  filters={threatFilters}
                  totalSize={totalSize % 21 !== 0 ? events.length : events.length + 1}
                  isLastPage={isLastPage}
                  cacheSize={events.length}
                  from={'incidentList'}
                />
              </div>
            </>
          </LoaderWrapper>
        </div>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  events: state.entities.events.list,
  threatFilters: state.entities.events.threat.filters,
  loading: state.entities.events.loading,
  totalSize: state.entities.events.totalSize,
  isLastPage: state.entities.events.isLastPage,
  selectedCustomer: state.entities.customers.selected,
})

const mapDispatchToProps = (dispatch) => ({
  loadNextEvents: (offset, customerId, defaultCustomerId, isGlobalView) =>
    dispatch(loadNextEvents(offset, customerId, defaultCustomerId, isGlobalView)),
  loadPrevEvents: (offset, customerId, defaultCustomerId, isGlobalView) =>
    dispatch(loadPrevEvents(offset, customerId, defaultCustomerId, isGlobalView)),
  loadEvents: (filters, selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(loadEvents(filters, selectedId, defaultCustomerId, isGlobalView)),
  setThreatFiltersInStore: (filters) => dispatch(setThreatFiltersInStore(filters)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreatFeedTable)
